import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Modal from 'react-modal';
import NoteEditor from './NoteEditor';
import AttachmentList from './AttachmentList';
import {
    createQuestion,
    isUploadConfigured,
    updateQuestion,
    uploadAttachment,
    createOrUpdatePlayset,
} from '../integrations/quizApiAdapter';
import { deriveKindFromMime, normalizeAttachments } from '../utils/attachments';

Modal.setAppElement('#root');

const ANSWER_LABELS = ['A', 'B', 'C', 'D'];

const emptyQuestion = {
    frage: '',
    antworten: ['', '', '', ''],
    richtigeAntwort: 0,
    kategorie: '',
    schwierigkeitsgrad: 1,
    attachments: [],
    note: '',
    playsetIds: [],
    lastPlayedAt: null,
};

function QuestionEditor({
    isEditing = false,
    questionId = null,
    initialQuestion = emptyQuestion,
    playsets = [],
    existingCategories = [],
    onQuestionSaved,
    onSaveAndNext,
    onCancelEdit,
}) {
    const [frage, setFrage] = useState(initialQuestion.frage || '');
    const [antworten, setAntworten] = useState(initialQuestion.antworten || ['', '', '', '']);
    const [richtigeAntwort, setRichtigeAntwort] = useState(initialQuestion.richtigeAntwort ?? 0);
    const [kategorie, setKategorie] = useState(initialQuestion.kategorie || '');
    const [schwierigkeitsgrad, setSchwierigkeitsgrad] = useState(initialQuestion.schwierigkeitsgrad ?? 1);
    const [attachments, setAttachments] = useState(() =>
        normalizeAttachments(initialQuestion.attachments || [], initialQuestion.id)
    );
    const [note, setNote] = useState(initialQuestion.note || '');
    // playsetIds als Numbers normalisiert
    const [playsetIds, setPlaysetIds] = useState(
        (initialQuestion.playsetIds || []).map(Number)
    );
    const [showsetQuery, setShowsetQuery] = useState('');
    const [localShowsets, setLocalShowsets] = useState([]);
    const [creatingShowset, setCreatingShowset] = useState(false);
    const [showsetMessage, setShowsetMessage] = useState('');
    const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
    const [uploadState, setUploadState] = useState({ loading: false, error: '' });
    const [attachmentForm, setAttachmentForm] = useState({ file: null });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // Autocomplete state
    const [catFocused, setCatFocused] = useState(false);

    const uploadEnabled = isUploadConfigured();

    // Wenn sich die Playsets-Prop ändert, localShowsets updaten
    // UND dabei die bereits ausgewählten IDs beibehalten
    useEffect(() => {
        setLocalShowsets(Array.isArray(playsets) ? playsets : []);
    }, [playsets]);

    const handleAntwortChange = (index, value) => {
        const next = [...antworten];
        next[index] = value;
        setAntworten(next);
    };

    const resetForm = () => {
        setFrage('');
        setAntworten(['', '', '', '']);
        setRichtigeAntwort(0);
        setKategorie('');
        setSchwierigkeitsgrad(1);
        setAttachments([]);
        setNote('');
        setPlaysetIds([]);
        setSaveError('');
    };

    const buildPayload = () => ({
        frage,
        antworten,
        richtigeAntwort,
        kategorie,
        schwierigkeitsgrad,
        attachments,
        note,
        playsetIds: playsetIds.map(Number),
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        try {
            const saved = isEditing && questionId
                ? await updateQuestion(questionId, buildPayload())
                : await createQuestion(buildPayload());
            onQuestionSaved && onQuestionSaved(saved);
        } catch (error) {
            setSaveError(error?.message || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAndNext = async () => {
        setSaving(true);
        setSaveError('');
        try {
            const saved = await createQuestion(buildPayload());
            onQuestionSaved && onQuestionSaved(saved);
            onSaveAndNext && onSaveAndNext(saved);
            resetForm();
            // Nur auf Nicht-Touch-Geräten fokussieren – auf Mobile würde die Tastatur automatisch erscheinen
            if (!window.matchMedia('(pointer: coarse)').matches) {
                setTimeout(() => {
                    const el = document.getElementById('qe-frage');
                    if (el) el.focus();
                }, 50);
            }
        } catch (error) {
            setSaveError(error?.message || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    // Showset-Toggle: Ein/Ausschalten per Klick
    const handleToggleShowset = useCallback((showsetId) => {
        const idNum = Number(showsetId);
        setPlaysetIds((prev) =>
            prev.includes(idNum)
                ? prev.filter((id) => id !== idNum)
                : [...prev, idNum]
        );
    }, []);

    const handleCreateShowset = async (name) => {
        if (!name.trim()) return;
        setCreatingShowset(true);
        setShowsetMessage('');
        try {
            const created = await createOrUpdatePlayset({ name: name.trim(), description: null, questionIds: [] });
            setLocalShowsets((prev) => [...prev, created]);
            // Direkt auswählen
            setPlaysetIds((prev) => {
                const idNum = Number(created.id);
                if (prev.includes(idNum)) return prev;
                return [...prev, idNum];
            });
            setShowsetQuery('');
        } catch (error) {
            setShowsetMessage(error?.message || 'Showset konnte nicht erstellt werden');
        } finally {
            setCreatingShowset(false);
        }
    };

    const filteredShowsets = useMemo(() => {
        if (!showsetQuery.trim()) return localShowsets;
        return localShowsets.filter((ps) =>
            ps.name.toLowerCase().includes(showsetQuery.toLowerCase())
        );
    }, [localShowsets, showsetQuery]);

    const hasExactShowsetMatch = useMemo(
        () => localShowsets.some((ps) => ps.name.toLowerCase() === showsetQuery.trim().toLowerCase()),
        [localShowsets, showsetQuery]
    );

    // Attachment-Handling
    const addAttachment = (item) => setAttachments((prev) => [...prev, item]);

    const resetAttachmentModal = () => {
        setAttachmentForm({ file: null });
        setUploadState({ loading: false, error: '' });
        setAttachmentModalOpen(false);
    };

    const handleUploadAndAdd = async () => {
        if (!attachmentForm.file) return;
        setUploadState({ loading: true, error: '' });
        try {
            const uploaded = await uploadAttachment(attachmentForm.file);
            const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random()}`;
            const kind = deriveKindFromMime(uploaded.mime || attachmentForm.file.type) || 'file';
            addAttachment({ id, type: kind, kind, url: uploaded.url, title: uploaded.originalName || attachmentForm.file.name });
            resetAttachmentModal();
        } catch (error) {
            setUploadState({ loading: false, error: error?.message || 'Upload fehlgeschlagen' });
        }
    };

    // Kategorie-Autocomplete
    const catSuggestions = useMemo(() => {
        if (!kategorie.trim()) return [];
        return existingCategories
            .filter((c) => c.toLowerCase().includes(kategorie.toLowerCase()) && c !== kategorie)
            .slice(0, 6);
    }, [existingCategories, kategorie]);

    const isCreateMode = !isEditing;
    const missingShowset = isCreateMode && playsetIds.length === 0;

    const lastPlayedText = useMemo(() => {
        if (!initialQuestion.lastPlayedAt) return null;
        return new Date(initialQuestion.lastPlayedAt).toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    }, [initialQuestion.lastPlayedAt]);

    const diffLabels = { 1: 'Leicht', 2: 'Mittel', 3: 'Schwer' };
    const diffClasses = { 1: 'd1', 2: 'd2', 3: 'd3' };

    return (
        <form onSubmit={handleSubmit} className="section-stack">
            <div className="card-header">
                <h3 className="card-title">{isEditing ? 'Frage bearbeiten' : 'Neue Frage anlegen'}</h3>
                {lastPlayedText && (
                    <span className="text-muted" style={{ fontSize: '12px' }}>
                        Zuletzt gespielt: {lastPlayedText}
                    </span>
                )}
            </div>

            {/* ── Fragetext ──────────────────────────────────── */}
            <div className="input-group">
                <label htmlFor="qe-frage">Frage</label>
                <input
                    id="qe-frage"
                    type="text"
                    value={frage}
                    onChange={(e) => setFrage(e.target.value)}
                    placeholder="Was ist die Frage?"
                    required
                />
            </div>

            {/* ── Antworten A/B/C/D ──────────────────────────── */}
            <div className="input-group">
                <label>Antworten — richtige markieren</label>
                <div className="qv-answer-grid">
                    {[0, 1, 2, 3].map((index) => (
                        <div
                            key={index}
                            className={`qv-answer-item${richtigeAntwort === index ? ' correct' : ''}`}
                            onClick={() => setRichtigeAntwort(index)}
                        >
                            <div className="qv-answer-label">{ANSWER_LABELS[index]}</div>
                            <input
                                type="text"
                                className="qv-answer-text"
                                value={antworten[index]}
                                onChange={(e) => handleAntwortChange(index, e.target.value)}
                                placeholder={`Antwort ${ANSWER_LABELS[index]}`}
                                required
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Kategorie + Schwierigkeit ───────────────────── */}
            <div className="layout-two-column">
                <div className="input-group">
                    <label>Kategorie</label>
                    <div className="qv-autocomplete">
                        <input
                            type="text"
                            value={kategorie}
                            onChange={(e) => setKategorie(e.target.value)}
                            onFocus={() => setCatFocused(true)}
                            onBlur={() => setTimeout(() => setCatFocused(false), 150)}
                            placeholder="z.B. Sport, Geografie …"
                        />
                        {catFocused && catSuggestions.length > 0 && (
                            <div className="qv-autocomplete-dropdown">
                                {catSuggestions.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        className="qv-autocomplete-item"
                                        onMouseDown={() => { setKategorie(s); setCatFocused(false); }}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="input-group">
                    <label>Schwierigkeit</label>
                    <div className="qv-diff-segmented">
                        {[1, 2, 3].map((lvl) => (
                            <button
                                key={lvl}
                                type="button"
                                className={`${diffClasses[lvl]}${schwierigkeitsgrad === lvl ? ' active' : ''}`}
                                onClick={() => setSchwierigkeitsgrad(lvl)}
                                aria-pressed={schwierigkeitsgrad === lvl}
                            >
                                {diffLabels[lvl]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Showsets + Attachments nebeneinander ───────── */}
            <div className="qv-editor-cols">
                {/* Showsets */}
                <div className="input-group" style={{ gap: '8px' }}>
                    <label>Showsets</label>

                    {/* Such-/Erstellungsfeld */}
                    <input
                        type="text"
                        value={showsetQuery}
                        onChange={(e) => setShowsetQuery(e.target.value)}
                        placeholder="Showset suchen oder erstellen…"
                        aria-label="Showset suchen"
                    />

                    {/* Scrollbare Liste – aktive zuerst, dann Trennlinie, dann archivierte */}
                    <div className="qv-showset-list">
                        {(() => {
                            const active   = filteredShowsets.filter((ps) => ps.status !== 'archived');
                            const archived = filteredShowsets.filter((ps) => ps.status === 'archived');
                            const renderBtn = (ps) => {
                                const selected = playsetIds.includes(Number(ps.id));
                                return (
                                    <button
                                        key={ps.id}
                                        type="button"
                                        className={`qv-showset-btn${selected ? ' selected' : ''}${ps.status === 'archived' ? ' archived' : ''}`}
                                        onClick={() => handleToggleShowset(ps.id)}
                                    >
                                        <span className="qv-showset-check">{selected ? '✓' : ''}</span>
                                        {ps.name}
                                    </button>
                                );
                            };
                            if (filteredShowsets.length === 0 && !showsetQuery.trim()) {
                                return (
                                    <div className="text-muted" style={{ fontSize: '12px', padding: '6px 4px' }}>
                                        Keine Showsets vorhanden
                                    </div>
                                );
                            }
                            return (
                                <>
                                    {active.map(renderBtn)}
                                    {active.length > 0 && archived.length > 0 && (
                                        <div className="qv-showset-divider" />
                                    )}
                                    {archived.map(renderBtn)}
                                </>
                            );
                        })()}
                    </div>

                    {/* Neues Showset erstellen */}
                    {!hasExactShowsetMatch && showsetQuery.trim() && (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleCreateShowset(showsetQuery)}
                            disabled={creatingShowset}
                        >
                            {creatingShowset ? 'Erstellen…' : `"${showsetQuery.trim()}" erstellen`}
                        </button>
                    )}
                    {showsetMessage && (
                        <div className="text-muted" style={{ fontSize: '12px' }}>{showsetMessage}</div>
                    )}
                </div>

                {/* Attachments */}
                <div className="input-group" style={{ gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ margin: 0 }}>Attachments</label>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                                setAttachmentForm({ file: null });
                                setUploadState({ loading: false, error: '' });
                                setAttachmentModalOpen(true);
                            }}
                        >
                            + Hinzufügen
                        </button>
                    </div>
                    <AttachmentList
                        attachments={attachments}
                        onRemove={(index) => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                    />
                    {attachments.length === 0 && (
                        <div className="text-muted" style={{ fontSize: '12px' }}>Keine Attachments</div>
                    )}
                </div>
            </div>

            {/* ── Notiz ──────────────────────────────────────── */}
            <NoteEditor value={note} onChange={setNote} onSave={() => Promise.resolve()} />

            {/* ── Fehler ─────────────────────────────────────── */}
            {saveError && (
                <div style={{
                    color: '#f87171', fontSize: '13px', padding: '8px 12px',
                    borderRadius: '8px', background: 'rgba(248,113,113,0.1)',
                    border: '1px solid rgba(248,113,113,0.3)',
                }}>
                    {saveError}
                </div>
            )}

            {/* ── Aktionen ───────────────────────────────────── */}
            <div className="button-row">
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={missingShowset || saving}
                    aria-label="Frage speichern"
                >
                    {saving ? 'Speichern…' : (isEditing ? 'Änderungen speichern' : 'Speichern & Schließen')}
                </button>

                {!isEditing && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleSaveAndNext}
                        disabled={missingShowset || saving}
                    >
                        Speichern & Nächste
                    </button>
                )}

                {isEditing && onCancelEdit && (
                    <button type="button" className="btn btn-ghost" onClick={onCancelEdit} disabled={saving}>
                        Abbrechen
                    </button>
                )}

                {missingShowset && (
                    <span className="text-muted" style={{ fontSize: '13px', alignSelf: 'center' }}>
                        Bitte mind. ein Showset wählen
                    </span>
                )}
            </div>

            {/* ── Upload-Modal ────────────────────────────────── */}
            <Modal
                isOpen={attachmentModalOpen}
                onRequestClose={resetAttachmentModal}
                className="modal-content"
                overlayClassName="modal-overlay"
                contentLabel="Attachment hinzufügen"
            >
                <div className="card-header">
                    <h3 className="card-title">Attachment hinzufügen</h3>
                </div>
                <div className="section-stack">
                    {!uploadEnabled && <div className="text-muted">Upload nicht konfiguriert</div>}
                    {uploadEnabled && (
                        <>
                            <div className="input-group">
                                <label>Datei auswählen</label>
                                <input
                                    type="file"
                                    accept="image/*,video/*,audio/*"
                                    onChange={(e) => setAttachmentForm({ file: e.target.files?.[0] || null })}
                                />
                            </div>
                            {uploadState.error && <div className="text-muted">{uploadState.error}</div>}
                            <div className="button-row">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!attachmentForm.file || uploadState.loading}
                                    onClick={handleUploadAndAdd}
                                >
                                    {uploadState.loading ? 'Upload…' : 'Hinzufügen'}
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={resetAttachmentModal}>
                                    Abbrechen
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </form>
    );
}

export default QuestionEditor;
